import { useCallback, useMemo } from 'react'

import { useQuery } from 'modules/core/hooks/useQuery'
import {
  IntegrationsAgreggatedSyncableStateValuesEnum,
  ReadSyncableStateStatisticsDocument,
  SyncableStateUpdatedDocument,
} from 'generated/resources/graphql'
import { print } from 'graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useLeadingTrailingDebounce } from 'modules/integrations/hooks/useLeadingTrailingDebounce'
import { useSyncableStateStatistics } from 'modules/integrations/hooks/useSyncableStateStatistics'

export type SyncBarStatus = 'pending' | 'syncing' | 'failed' | 'synced'

export type StatusCount = {
  status: IntegrationsAgreggatedSyncableStateValuesEnum
  count: number
  label: string
}

const translateStatusLabel = (status: IntegrationsAgreggatedSyncableStateValuesEnum): string => {
  switch (status) {
    case 'failed':
      return i18n.t('integrations.filters.sync_status.failed')
    case 'partially_synced':
      return i18n.t('integrations.filters.sync_status.partially_synced')
    case 'outdated':
      return i18n.t('integrations.filters.sync_status.outdated')
    case 'pending':
      return i18n.t('integrations.filters.sync_status.pending')
    case 'synced':
      return i18n.t('integrations.filters.sync_status.synced')
    case 'syncing':
      return i18n.t('integrations.filters.sync_status.syncing')
  }
}

const deriveSyncBarStatus = (
  statusCounts: Array<{ status: string; count: number }>,
  isSyncing: boolean
): SyncBarStatus => {
  const getCount = (status: string) => statusCounts.find((s) => s.status === status)?.count ?? 0

  if (getCount('syncing') > 0 || isSyncing) return 'syncing'
  if (getCount('failed') > 0 || getCount('partially_synced') > 0) return 'failed'
  if (getCount('pending') > 0 || getCount('outdated') > 0) return 'pending'
  return 'synced'
}

const translateSyncBarStatus = (status: SyncBarStatus): string => {
  switch (status) {
    case 'pending':
      return i18n.t('integrations.sync_bar.status.pending')
    case 'syncing':
      return i18n.t('integrations.sync_bar.status.syncing')
    case 'failed':
      return i18n.t('integrations.sync_bar.status.failed')
    case 'synced':
      return i18n.t('integrations.sync_bar.status.synced')
  }
}

export const useSyncBarData = () => {
  const {
    dataCollectionSourceState,
    marketplaceIntegration,
    syncEnabled,
    configuration,
    isSyncing,
  } = usePrivateIntegrationsSyncContext()

  const pollingEnabled = configuration.pollingEnabled ?? false

  const document = syncEnabled ? dataCollectionSourceState.document : null
  const mappedBaseVariablesJson = syncEnabled
    ? dataCollectionSourceState.mappedBaseVariablesJson
    : null

  const graphqlVariablesJson = useMemo(
    () =>
      document != null
        ? JSON.stringify({
            query: print(document),
            variables: {
              ...(mappedBaseVariablesJson ? JSON.parse(mappedBaseVariablesJson) : {}),
              marketplaceIntegrationId: Number(marketplaceIntegration?.id ?? 0),
              includeSyncableState: Boolean(true),
              syncableViaIntegrations: Boolean(true),
            },
          })
        : undefined,
    [document, mappedBaseVariablesJson, marketplaceIntegration?.id]
  )

  const skip = !syncEnabled

  // --- Polling mode (professionals/bookkeeper view) ---
  // Reuses useSyncableStateStatistics which already handles useLazyQuery + useUnsafeEffect + usePolling
  const { statistics: pollingStatistics } = useSyncableStateStatistics({
    skip: !pollingEnabled,
    isSyncing,
  })

  // --- Subscription mode (standard view): useQuery + subscription refetch ---
  const subscriptionQueryVariables = useMemo(
    () => ({
      graphqlVariablesJson: graphqlVariablesJson ?? undefined,
      origin: configuration.origin,
    }),
    [graphqlVariablesJson, configuration.origin]
  )

  const { data: subscriptionData, refetch: refetchSubscription } = useQuery(
    ReadSyncableStateStatisticsDocument,
    {
      variables: subscriptionQueryVariables,
      skip: skip || pollingEnabled,
    }
  )

  const refetch = useCallback(() => {
    if (skip || pollingEnabled) return
    void refetchSubscription()
  }, [skip, pollingEnabled, refetchSubscription])

  const DEBOUNCE_DELAY_MS = 1000
  const debouncedRefetch = useLeadingTrailingDebounce(refetch, DEBOUNCE_DELAY_MS)

  // Use subscription-based updates when polling is disabled (standard mode)
  useSubscription(SyncableStateUpdatedDocument, {
    skip: !syncEnabled || pollingEnabled,
    onData: () => {
      debouncedRefetch()
    },
  })

  const subscriptionStatistics =
    subscriptionData?.integrations?.syncableStateStatisticsConnection?.nodes?.[0]

  const statistics = pollingEnabled ? pollingStatistics : subscriptionStatistics
  const rawStatusCounts = useMemo(() => statistics?.statusCounts ?? [], [statistics])

  const syncStatus: SyncBarStatus | null = useMemo(() => {
    if (!syncEnabled) return null
    if (rawStatusCounts.length === 0) return isSyncing ? 'syncing' : null
    return deriveSyncBarStatus(rawStatusCounts, isSyncing)
  }, [syncEnabled, isSyncing, rawStatusCounts])

  const syncStatusLabel = useMemo(
    () => (syncStatus ? translateSyncBarStatus(syncStatus) : null),
    [syncStatus]
  )

  const statusCounts = useMemo<StatusCount[]>(() => {
    if (!syncEnabled) return []

    const ALL_STATUSES: IntegrationsAgreggatedSyncableStateValuesEnum[] = [
      'failed',
      'partially_synced',
      'outdated',
      'pending',
      'synced',
      'syncing',
    ]

    return ALL_STATUSES.map((status) => ({
      status,
      count: rawStatusCounts.find((s) => s.status === status)?.count ?? 0,
      label: translateStatusLabel(status),
    })).filter((entry) => entry.count > 0)
  }, [syncEnabled, rawStatusCounts])

  return {
    syncStatus,
    syncStatusLabel,
    statusCounts,
  }
}
